import i18next from "i18next";
import { groupBy } from "es-toolkit/compat";

export interface PracticeLocation {
  id: string;
  name: string;
  city: string;
  state: string;
}

export interface HierarchicalLocationItem {
  id: string;
  label: string;
  children?: Array<{
    id: string;
    label: string;
  }>;
}

export function transformLocationsHierarchical(
  locations: PracticeLocation[],
): HierarchicalLocationItem[] {
  if (!locations || locations.length === 0) {
    return [];
  }

  // Group locations by state
  const groupedByState = groupBy(locations, "state");

  const result: HierarchicalLocationItem[] = [];

  Object.entries(groupedByState).forEach(([state, stateLocations]) => {
    if (stateLocations.length === 1) {
      // If only one location in this state, show it directly without nesting
      const location = stateLocations[0];
      result.push({
        id: location.id,
        label: `${location.name} - ${location.city}, ${state}`,
      });
    } else {
      // Multiple locations in state, show nested structure
      result.push({
        id: `state-${state}`,
        label: i18next.t("locationTransform.stateLocations", { state }),
        children: stateLocations.map((location) => ({
          id: location.id,
          label: `${location.name} - ${location.city}, ${state}`,
        })),
      });
    }
  });

  return result;
}

export interface CityGroup {
  city: string;
  locations: PracticeLocation[];
}

export function transformLocationsByCity(locations: PracticeLocation[]): CityGroup[] {
  if (!locations || locations.length === 0) {
    return [];
  }

  const groupedByCity = groupBy(locations, "city");

  return Object.entries(groupedByCity)
    .map(([city, cityLocations]) => ({
      city,
      locations: [...cityLocations].sort((left, right) => left.name.localeCompare(right.name)),
    }))
    .sort((left, right) => left.city.localeCompare(right.city));
}
